import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import type { Notice } from './schemas';

/**
 * §6's `NoticePage`: the DPDP notice as a data principal reads it.
 *
 * The api sends the text *and* its i18n key for every line (`dpdp.notice.*`), so this
 * renders `t(key, { defaultValue: text })`: the browser's own bundle when it has the
 * locale, the server's text otherwise. That matters for the client app's `/privacy`,
 * which is public and may be the first page a phone ever loads from this salon — and for
 * v2, where Hindi, Marathi and Gujarati arrive as sibling bundles under the same keys
 * with no change here.
 *
 * `version` is shown because rule 7 versions the text and a consent records the version
 * it was given under; a client comparing the two has to be able to.
 */
export function NoticeView({ notice }: { notice: Notice }): JSX.Element {
  const { t } = useTranslation('dpdp');

  return (
    <article data-testid="notice" data-version={notice.version} className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold">{t('dpdp.privacy.title')}</h1>
        <p className="text-sm text-muted-foreground" data-testid="notice-version">
          {t('dpdp.privacy.version', { version: notice.version })}
        </p>
      </header>

      {notice.sections.map((section) => (
        <section key={section.key} data-testid={`notice-section-${section.key}`}>
          <h2 className="font-semibold">
            {t(`${section.key}.title`, { defaultValue: section.title })}
          </h2>
          {section.body.map((line, index) => (
            <p key={`${section.key}.body.${String(index)}`} className="mt-1 text-foreground">
              {t(`${section.key}.body.${String(index)}`, { defaultValue: line })}
            </p>
          ))}
          {section.items.length > 0 ? (
            <ul className="mt-2 list-disc space-y-1 pl-5 text-foreground">
              {section.items.map((item) => (
                <li key={item.key}>{t(item.key, { defaultValue: item.text })}</li>
              ))}
            </ul>
          ) : null}
        </section>
      ))}

      <section data-testid="notice-grievance">
        <h2 className="font-semibold">{t('dpdp.privacy.grievance')}</h2>
        <p className="text-foreground">
          {[notice.grievance.name, notice.grievance.email, notice.grievance.phone]
            .filter((part): part is string => part !== null && part !== '')
            .join(' · ') || t('dpdp.privacy.frontDesk')}
        </p>
        <p className="mt-2">
          <a className="underline" href={notice.board_url} rel="noreferrer noopener">
            {t('dpdp.privacy.board')}
          </a>
        </p>
      </section>
    </article>
  );
}
